import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { DateTime } from 'luxon';
import { getStartSessionTimestamp, Session } from '@mindly/ui-components';
import { ConsultationType } from '../../pages/types';
import { consultationsSortedByTimeSelector } from '../../redux/consultations/selector';
import { globalVariables } from '../../redux/firestoreGlobalVariables/selector';
import { userSelector } from '../../redux/user/selector';
import {
  setSessionForReview,
  setSubscriptionSessionReviewModalIsOpen,
} from '../../redux/consultationReviews/consultationsReviews.reducer';
import {
  getTrialSessionReviewPromoCode,
  getTrialSessionReviewContinueTherapy,
} from '../utils/manageTrialSessionReview';
import { getReviewedSessions, mergeSession } from '../utils/manageSubscriptionSessionReview';
import { getReviewedSessions as getReviewedTrialSessions } from '../utils/manageTrialSessionReview';
import { usePushNotifications } from '../context/PushNotificationsContext';

export const useSubscriptionSessionReviewTimer = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const pushNotificationsState = usePushNotifications();
  const globalVars = useSelector(globalVariables);
  const featureStartDate = globalVars?.features?.session_reviews?.start_date?._seconds
    ? DateTime.fromMillis(globalVars?.features?.session_reviews?.start_date._seconds * 1000)
    : DateTime.utc(2025, 6, 1, 9, 0, 0);

  const client = useSelector(userSelector);
  const consultations = useSelector(consultationsSortedByTimeSelector);
  const [localConsultations, setLocalConsultations] = useState<(ConsultationType | Session)[]>([]);
  const [intervalRef, setIntervalRef] = useState<any>(null);
  const minHours = 0.5;
  const maxHours = 2;
  const refreshInterval = 2.53 * 1000;

  useEffect(() => {
    if (client?.id && consultations.length > 0) {
      clearInterval(intervalRef);
      setIntervalRef(null);
      setLocalConsultations(consultations.filter((con) => con.subscription_id && !con.session_review));
    }
  }, [consultations, client]);

  useEffect(() => {
    if (!intervalRef && localConsultations.length > 0 && client?.id) {
      setIntervalRef(
        setInterval(() => {
          const isPromoCodeModalOpen = getTrialSessionReviewPromoCode();
          const isContinueTherapyOpened = getTrialSessionReviewContinueTherapy();

          if (isPromoCodeModalOpen || isContinueTherapyOpened) {
            return;
          }

          const urlName = 'video-call';
          const videoIsActive =
            document?.location?.href?.includes(urlName) ||
            document?.location?.pathname?.includes(urlName) ||
            window?.location?.pathname?.includes(urlName) ||
            location?.pathname?.includes(urlName);

          if (videoIsActive || pushNotificationsState.isOpenPushNotificationModal) {
            return;
          }

          const reviewedSessions = getReviewedSessions();
          const reviewedTrialSessions = getReviewedTrialSessions();

          const hasOpenSession = reviewedSessions.some((session) => session.isOpen);
          const hasOpenTrialSession = reviewedTrialSessions.some((session) => session.isOpen);
          if (hasOpenSession || hasOpenTrialSession) {
            return;
          }

          const sessionsToReview = localConsultations.filter((con: ConsultationType | Session) => {
            if (reviewedSessions.some((reviewed) => reviewed.sessionId === con.id)) {
              return false;
            }

            const sessionStartTime = getStartSessionTimestamp(con.consultation_time);
            if (DateTime.fromMillis(sessionStartTime).diff(featureStartDate, 'hours').hours < 0) {
              return false;
            }

            const consultationTime =
              typeof con.consultation_time === 'string'
                ? DateTime.fromISO(con.consultation_time)
                : DateTime.fromSeconds(con.consultation_time._seconds);

            const timeDiff = DateTime.local().diff(consultationTime, 'hours').hours;

            return minHours <= timeDiff && timeDiff < maxHours;
          });

          if (sessionsToReview.length > 0) {
            const sessionToReview = sessionsToReview[0];
            const consultationTime =
              typeof sessionToReview.consultation_time === 'string'
                ? DateTime.fromISO(sessionToReview.consultation_time)
                : DateTime.fromSeconds(sessionToReview.consultation_time._seconds);

            dispatch(setSessionForReview(sessionToReview));
            dispatch(setSubscriptionSessionReviewModalIsOpen(true));

            mergeSession(sessionToReview.id, {
              sessionStartTimestamp: consultationTime.toMillis(),
              isOpen: true,
              userId: client?.id ?? '',
            });
          }
        }, refreshInterval)
      );
    }
  }, [localConsultations, pushNotificationsState.isOpenPushNotificationModal, client?.id]);

  return () => {
    if (intervalRef) {
      clearInterval(intervalRef);
      setIntervalRef(null);
    }
  };
};
